/* ==========================================================================
   ETRONNIX DESIGNS — theme.js
   Vanilla JS, no dependencies. Loaded with `defer`.
   Everything is built as small custom elements / delegated listeners so each
   feature only runs where its markup exists. All cart work uses Shopify's
   AJAX Cart API + Section Rendering API; recommendations use the Product
   Recommendations API; search suggestions use the Predictive Search API.
   ========================================================================== */
(() => {
  'use strict';

  const T = window.theme || { routes: {}, strings: {}, freeShipping: {} };
  const R = T.routes || {};
  const S = T.strings || {};
  const root = (R.root || '/').replace(/\/?$/, '/');
  const $ = (sel, ctx = document) => ctx.querySelector(sel);
  const $$ = (sel, ctx = document) => Array.from(ctx.querySelectorAll(sel));
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ------------------------------------------------------------------ utils */
  const debounce = (fn, wait = 300) => {
    let t;
    return (...args) => { clearTimeout(t); t = setTimeout(() => fn(...args), wait); };
  };

  const store = {
    get(key, fallback) {
      try { const v = localStorage.getItem(key); return v ? JSON.parse(v) : fallback; } catch (e) { return fallback; }
    },
    set(key, value) {
      try { localStorage.setItem(key, JSON.stringify(value)); } catch (e) { /* storage full / private mode */ }
    }
  };

  function formatMoney(cents, format = T.moneyFormat || '${{amount}}') {
    if (typeof cents === 'string') cents = cents.replace('.', '');
    cents = parseInt(cents, 10) || 0;
    const fmt = (n, decimals, thousands = ',', decimal = '.') => {
      const fixed = (n / 100).toFixed(decimals);
      const [whole, frac] = fixed.split('.');
      return whole.replace(/\B(?=(\d{3})+(?!\d))/g, thousands) + (frac ? decimal + frac : '');
    };
    return format.replace(/\{\{\s*(\w+)\s*\}\}/, (_, key) => {
      switch (key) {
        case 'amount_no_decimals': return fmt(cents, 0);
        case 'amount_with_comma_separator': return fmt(cents, 2, '.', ',');
        case 'amount_no_decimals_with_comma_separator': return fmt(cents, 0, '.', ',');
        case 'amount_with_apostrophe_separator': return fmt(cents, 2, "'", '.');
        case 'amount_with_space_separator': return fmt(cents, 2, ' ', ',');
        default: return fmt(cents, 2);
      }
    });
  }

  async function fetchText(url, options) {
    const res = await fetch(url, options);
    if (!res.ok) throw new Error(res.status);
    return res.text();
  }

  const parseHTML = (html) => new DOMParser().parseFromString(html, 'text/html');

  let toastTimer;
  function toast(message, isError = false) {
    const el = $('#Toast');
    if (!el || !message) return;
    el.textContent = message;
    el.classList.toggle('is-error', isError);
    el.hidden = false;
    clearTimeout(toastTimer);
    toastTimer = setTimeout(() => { el.hidden = true; }, 3200);
  }

  const lock = { count: 0 };
  function lockScroll(on) {
    lock.count = Math.max(0, lock.count + (on ? 1 : -1));
    document.body.classList.toggle('is-locked', lock.count > 0);
  }

  const focusableSel = 'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select, textarea, [tabindex]:not([tabindex="-1"])';
  function trapFocus(container, e) {
    if (e.key !== 'Tab') return;
    const items = $$(focusableSel, container).filter((el) => el.offsetParent !== null);
    if (!items.length) return;
    const first = items[0];
    const last = items[items.length - 1];
    if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
  }

  /* ------------------------------------------------------ reveal on scroll */
  function initReveal(scope = document) {
    const items = $$('[data-reveal]:not(.is-visible)', scope);
    if (!items.length) return;
    if (!('IntersectionObserver' in window) || reduceMotion || !document.documentElement.classList.contains('anim-reveal')) {
      items.forEach((el) => el.classList.add('is-visible'));
      return;
    }
    const io = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) { entry.target.classList.add('is-visible'); io.unobserve(entry.target); }
      });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.05 });
    items.forEach((el) => io.observe(el));
  }

  /* --------------------------------------------------------- announcement */
  class AnnouncementRotator extends HTMLElement {
    connectedCallback() {
      this.msgs = $$('.announcement__msg', this);
      if (this.msgs.length < 2 || this.dataset.autoplay !== 'true' || this.classList.contains('announcement__track--inline') && window.innerWidth >= 990) return;
      this.i = 0;
      this.timer = setInterval(() => this.next(), parseInt(this.dataset.interval, 10) || 5000);
      this.addEventListener('mouseenter', () => { this.paused = true; });
      this.addEventListener('mouseleave', () => { this.paused = false; });
    }
    disconnectedCallback() { clearInterval(this.timer); }
    next() {
      if (this.paused) return;
      this.msgs[this.i].classList.remove('is-active');
      this.i = (this.i + 1) % this.msgs.length;
      this.msgs[this.i].classList.add('is-active');
    }
  }
  customElements.define('announcement-rotator', AnnouncementRotator);

  /* --------------------------------------------------------- sticky header */
  class StickyHeader extends HTMLElement {
    connectedCallback() {
      this.mode = this.dataset.sticky;
      this.wrapper = this.closest('.shopify-section') || this;
      if (this.mode === 'none') return;
      document.body.classList.add('has-sticky-header');
      this.wrapper.style.position = 'sticky';
      this.wrapper.style.top = '0';
      this.lastY = window.scrollY;
      const setOffset = () => document.documentElement.style.setProperty('--header-offset', `${this.offsetHeight}px`);
      setOffset();
      window.addEventListener('resize', debounce(setOffset, 150));
      window.addEventListener('scroll', () => requestAnimationFrame(() => this.onScroll()), { passive: true });
    }
    onScroll() {
      const y = window.scrollY;
      this.classList.toggle('is-scrolled', y > 10);
      if (this.mode === 'scroll-up') {
        const hide = y > this.lastY && y > this.offsetHeight + 60 && !document.body.classList.contains('is-locked');
        this.wrapper.style.transform = hide ? 'translateY(-100%)' : '';
      }
      this.lastY = y;
    }
  }
  customElements.define('sticky-header', StickyHeader);

  /* --------------------------------------------------------------- drawers */
  class BaseDrawer extends HTMLElement {
    connectedCallback() {
      this.addEventListener('click', (e) => { if (e.target.closest('[data-close]')) this.close(); });
      this.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') this.close();
        trapFocus(this.querySelector('.drawer__panel') || this, e);
      });
    }
    open(trigger) {
      if (this.classList.contains('is-open')) return;
      this.trigger = trigger || document.activeElement;
      this.classList.add('is-open');
      this.setAttribute('aria-hidden', 'false');
      lockScroll(true);
      const panel = this.querySelector('.drawer__panel');
      setTimeout(() => panel && panel.focus(), 60);
    }
    close() {
      if (!this.classList.contains('is-open')) return;
      this.classList.remove('is-open');
      this.setAttribute('aria-hidden', 'true');
      lockScroll(false);
      if (this.trigger && this.trigger.focus) this.trigger.focus();
    }
  }
  class MenuDrawer extends BaseDrawer {}
  customElements.define('menu-drawer', MenuDrawer);

  class CartDrawer extends BaseDrawer {
    renderContents(html) {
      const doc = parseHTML(html);
      const fresh = doc.querySelector('cart-drawer');
      if (!fresh) return;
      this.innerHTML = fresh.innerHTML;
      this.dataset.itemCount = fresh.dataset.itemCount;
      updateCartCount(parseInt(fresh.dataset.itemCount, 10) || 0);
      initCartExtras(this);
    }
  }
  customElements.define('cart-drawer', CartDrawer);

  document.addEventListener('click', (e) => {
    const opener = e.target.closest('[data-drawer-open]');
    if (opener) {
      const drawer = document.getElementById(opener.dataset.drawerOpen);
      if (drawer && drawer.open) { e.preventDefault(); drawer.open(opener); }
    }
    const cartToggle = e.target.closest('[data-cart-toggle]');
    if (cartToggle && T.cartType === 'drawer' && !document.body.classList.contains('template-cart')) {
      const drawer = $('#CartDrawer');
      if (drawer) { e.preventDefault(); drawer.open(cartToggle); }
    }
    const back = e.target.closest('[data-back-to-top]');
    if (back) { e.preventDefault(); window.scrollTo({ top: 0, behavior: reduceMotion ? 'auto' : 'smooth' }); }
  });

  /* Escape closes any open drawer / modal, even if focus moved (e.g. after a cart re-render) */
  document.addEventListener('keydown', (e) => {
    if (e.key !== 'Escape') return;
    $$('.drawer.is-open, facet-drawer.is-open').forEach((d) => d.close && d.close());
    const qv = $('#QuickView'); if (qv && !qv.hidden && qv.close) qv.close();
  });

  function updateCartCount(count) {
    $$('[data-cart-count]').forEach((el) => {
      el.textContent = count;
      el.toggleAttribute('data-empty', count === 0);
      el.classList.remove('bump'); void el.offsetWidth; el.classList.add('bump');
    });
  }

  /* ---------------------------------------------- category nav dropdowns */
  $$('.catnav__item.has-dropdown').forEach((item) => {
    const dd = $('.catnav__dropdown', item);
    let t;
    const show = () => {
      clearTimeout(t);
      const r = item.getBoundingClientRect();
      dd.style.left = `${Math.min(r.left, window.innerWidth - 240)}px`;
      dd.style.top = `${r.bottom}px`;
      item.classList.add('is-open');
    };
    const hide = () => { t = setTimeout(() => item.classList.remove('is-open'), 120); };
    item.addEventListener('mouseenter', show);
    item.addEventListener('mouseleave', hide);
    item.addEventListener('focusin', show);
    item.addEventListener('focusout', hide);
    window.addEventListener('scroll', () => item.classList.remove('is-open'), { passive: true });
  });

  /* ----------------------------------------------------- predictive search */
  class PredictiveSearch extends HTMLElement {
    connectedCallback() {
      this.input = $('input[type="search"]', this);
      this.results = $('[data-results]', this);
      this.clearBtn = $('[data-clear]', this);
      if (!this.input) return;
      this.clearBtn && this.clearBtn.addEventListener('click', () => { this.input.value = ''; this.close(); this.input.focus(); this.clearBtn.hidden = true; });
      if (this.dataset.enabled !== 'true' || !R.predictiveSearch) return;
      this.input.addEventListener('input', debounce(() => this.onInput(), 220));
      this.input.addEventListener('focus', () => { if (this.input.value.trim().length > 1 && this.results.innerHTML) this.openResults(); });
      this.input.addEventListener('keydown', (e) => this.onKey(e));
      document.addEventListener('click', (e) => { if (!this.contains(e.target)) this.close(); });
      this.cache = {};
    }
    async onInput() {
      const q = this.input.value.trim();
      if (this.clearBtn) this.clearBtn.hidden = !q;
      if (q.length < 2) { this.close(); return; }
      if (this.cache[q]) { this.render(this.cache[q], q); return; }
      if (this.controller) this.controller.abort();
      this.controller = new AbortController();
      const params = new URLSearchParams({
        q,
        'resources[type]': 'product,collection,query',
        'resources[limit]': '6',
        'resources[options][unavailable_products]': 'last',
        'resources[options][fields]': 'title,product_type,variants.sku,vendor,tag,variants.title'
      });
      try {
        const res = await fetch(`${R.predictiveSearch}.json?${params}`, { signal: this.controller.signal });
        if (!res.ok) throw new Error(res.status);
        const data = await res.json();
        this.cache[q] = data.resources.results;
        this.render(this.cache[q], q);
      } catch (err) {
        if (err.name !== 'AbortError') this.close();
      }
    }
    render(results, q) {
      const esc = (s) => String(s || '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
      const mark = (s) => esc(s).replace(new RegExp(`(${q.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'ig'), '<mark>$1</mark>');
      const products = results.products || [];
      const queries = results.queries || [];
      const collections = results.collections || [];
      let html = '';
      if (queries.length) {
        html += `<div class="predictive__group"><ul class="predictive__list" role="listbox">${queries.slice(0, 4).map((s) =>
          `<li class="predictive__item" role="option"><a href="${esc(s.url)}"><span class="predictive__text"><span class="predictive__title">${s.styled_text || mark(s.text)}</span></span></a></li>`).join('')}</ul></div>`;
      }
      if (products.length) {
        html += `<div class="predictive__group"><p class="predictive__heading">${esc(S.suggestProducts || 'Products')}</p><ul class="predictive__list" role="listbox">${products.map((p) => {
          const img = p.featured_image && p.featured_image.url ? `<img class="predictive__img" src="${esc(p.featured_image.url)}&width=120" alt="" width="48" height="48" loading="lazy">` : '<span class="predictive__img"></span>';
          const price = p.price ? formatMoney(String(p.price).includes('.') ? Math.round(parseFloat(p.price) * 100) : p.price) : '';
          return `<li class="predictive__item" role="option"><a href="${esc(p.url)}">${img}<span class="predictive__text"><span class="predictive__title">${mark(p.title)}</span><span class="predictive__meta">${esc(p.vendor || '')}${p.type ? ' · ' + esc(p.type) : ''}</span></span><span class="predictive__price">${price}</span></a></li>`;
        }).join('')}</ul></div>`;
      }
      if (collections.length) {
        html += `<div class="predictive__group"><p class="predictive__heading">${esc(S.suggestCollections || 'Categories')}</p><ul class="predictive__list">${collections.slice(0, 3).map((c) =>
          `<li class="predictive__item"><a href="${esc(c.url)}"><span class="predictive__text"><span class="predictive__title">${mark(c.title)}</span></span></a></li>`).join('')}</ul></div>`;
      }
      if (!html) html = `<p class="predictive__empty">${esc((S.suggestEmpty || '').replace('[q]', q))}</p>`;
      html += `<a class="predictive__all" href="${R.search}?type=product&options%5Bprefix%5D=last&q=${encodeURIComponent(q)}">${esc((S.suggestAll || '').replace('[q]', q))} <span aria-hidden="true">→</span></a>`;
      this.results.innerHTML = html;
      this.openResults();
    }
    openResults() { this.results.hidden = false; this.input.setAttribute('aria-expanded', 'true'); }
    close() { if (!this.results) return; this.results.hidden = true; this.input && this.input.setAttribute('aria-expanded', 'false'); }
    onKey(e) {
      const links = $$('a', this.results);
      if (this.results.hidden || !links.length) return;
      const i = links.findIndex((a) => a.getAttribute('aria-selected') === 'true');
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        e.preventDefault();
        links.forEach((a) => a.removeAttribute('aria-selected'));
        const next = e.key === 'ArrowDown' ? (i + 1) % links.length : (i - 1 + links.length) % links.length;
        links[next].setAttribute('aria-selected', 'true');
        links[next].scrollIntoView({ block: 'nearest' });
      } else if (e.key === 'Enter' && i > -1) {
        e.preventDefault(); window.location.href = links[i].href;
      } else if (e.key === 'Escape') { this.close(); }
    }
  }
  customElements.define('predictive-search', PredictiveSearch);

  /* ------------------------------------------------------- cart API layer */
  const Cart = {
    sections() {
      const ids = ['cart-drawer'];
      const main = $('#MainCart');
      if (main && main.dataset.section) ids.push(main.dataset.section);
      return ids;
    },
    async add(items, sourceBtn) {
      const body = { items, sections: this.sections(), sections_url: window.location.pathname };
      const res = await fetch(`${R.cartAdd}.js`, { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify(body) });
      const data = await res.json();
      if (!res.ok || data.status) throw new Error(data.description || data.message || S.cartError);
      this.render(data.sections);
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: data }));
      if (T.cartType === 'page' && !document.body.classList.contains('template-cart')) {
        window.location.href = R.cart; return data;
      }
      const drawer = $('#CartDrawer');
      if (drawer && !document.body.classList.contains('template-cart')) drawer.open(sourceBtn);
      else toast(S.added);
      return data;
    },
    async change(line, quantity) {
      const res = await fetch(`${R.cartChange}.js`, { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ line, quantity, sections: this.sections(), sections_url: window.location.pathname }) });
      const data = await res.json();
      if (!res.ok || data.status) throw new Error(data.description || data.message || S.cartError);
      this.render(data.sections);
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: data }));
      return data;
    },
    render(sections) {
      if (!sections) return;
      const drawer = $('#CartDrawer');
      if (sections['cart-drawer'] && drawer && drawer.renderContents) drawer.renderContents(sections['cart-drawer']);
      const main = $('#MainCart');
      if (main && sections[main.dataset.section]) {
        const fresh = parseHTML(sections[main.dataset.section]).querySelector('#MainCart');
        if (fresh) { main.innerHTML = fresh.innerHTML; initCartExtras(main); }
      }
    }
  };
  window.EtronnixCart = Cart; // exposed for apps / custom code

  /* product forms (cards, product page, quick view, mini rows) */
  document.addEventListener('submit', async (e) => {
    const form = e.target.closest('form[data-product-form]');
    if (!form) return;
    e.preventDefault();
    const btn = e.submitter || $('[type="submit"]', form);
    const fd = new FormData(form);
    const item = { id: parseInt(fd.get('id'), 10), quantity: parseInt(fd.get('quantity'), 10) || 1 };
    const plan = fd.get('selling_plan');
    if (plan) item.selling_plan = parseInt(plan, 10);
    const props = {};
    for (const [k, v] of fd.entries()) { const m = k.match(/^properties\[(.+)\]$/); if (m && v) props[m[1]] = v; }
    if (Object.keys(props).length) item.properties = props;
    btn && btn.classList.add('is-loading');
    try {
      await Cart.add([item], btn);
      const qv = btn && btn.closest('quick-view-modal');
      if (qv) qv.close();
    } catch (err) {
      toast(err.message, true);
    } finally {
      btn && btn.classList.remove('is-loading');
    }
  });

  /* quantity steppers */
  class QuantityInput extends HTMLElement {
    connectedCallback() {
      this.input = $('input', this);
      this.addEventListener('click', (e) => {
        const b = e.target.closest('[data-step]');
        if (!b) return;
        const step = parseInt(b.dataset.step, 10);
        const min = parseInt(this.input.min || '0', 10);
        const max = this.input.max ? parseInt(this.input.max, 10) : Infinity;
        const next = Math.min(max, Math.max(min, (parseInt(this.input.value, 10) || 0) + step));
        if (next === parseInt(this.input.value, 10)) return;
        this.input.value = next;
        this.input.dispatchEvent(new Event('change', { bubbles: true }));
      });
    }
  }
  customElements.define('quantity-input', QuantityInput);

  /* cart line quantity / remove / save for later (drawer + cart page) */
  const lineChange = debounce(async (input) => {
    const line = parseInt(input.dataset.line, 10);
    const li = input.closest('.line');
    li && li.classList.add('is-updating');
    try { await Cart.change(line, parseInt(input.value, 10) || 0); }
    catch (err) { toast(err.message, true); li && li.classList.remove('is-updating'); }
  }, 350);
  document.addEventListener('change', (e) => {
    const input = e.target.closest('input[data-line]');
    if (input) lineChange(input);
  });
  document.addEventListener('click', async (e) => {
    const rm = e.target.closest('[data-remove]');
    if (rm) {
      e.preventDefault();
      const li = rm.closest('.line'); li && li.classList.add('is-updating');
      try { await Cart.change(parseInt(rm.dataset.remove, 10), 0); } catch (err) { toast(err.message, true); }
      return;
    }
    const save = e.target.closest('[data-save-later]');
    if (save) {
      e.preventDefault();
      const li = save.closest('.line');
      const img = li.querySelector('.line__img img');
      SavedForLater.add({
        variantId: parseInt(li.dataset.variantId, 10),
        handle: li.dataset.handle,
        title: (li.querySelector('.line__title') || {}).textContent || '',
        variant: (li.querySelector('.line__variant') || {}).textContent || '',
        image: img ? img.currentSrc || img.src : '',
        url: (li.querySelector('.line__title') || {}).href || '#'
      });
      li.classList.add('is-updating');
      try { await Cart.change(parseInt(save.dataset.saveLater, 10), 0); toast(S.savedForLater); } catch (err) { toast(err.message, true); }
    }
  });

  /* ---------------------------------------------------- save for later */
  const SavedForLater = {
    key: 'etx:saved',
    all() { return store.get(this.key, []); },
    add(item) { const list = this.all().filter((i) => i.variantId !== item.variantId); list.unshift(item); store.set(this.key, list.slice(0, 30)); this.renderAll(); },
    remove(id) { store.set(this.key, this.all().filter((i) => i.variantId !== id)); this.renderAll(); },
    renderAll() { $$('saved-for-later').forEach((el) => el.render && el.render()); }
  };
  class SavedForLaterEl extends HTMLElement {
    connectedCallback() {
      if (!T.saveForLater) return;
      this.render();
      this.addEventListener('click', async (e) => {
        const move = e.target.closest('[data-move]');
        const del = e.target.closest('[data-del]');
        if (move) {
          const id = parseInt(move.dataset.move, 10);
          move.classList.add('is-loading');
          try { await Cart.add([{ id, quantity: 1 }], move); SavedForLater.remove(id); toast(S.movedToCart); }
          catch (err) { toast(err.message, true); move.classList.remove('is-loading'); }
        }
        if (del) SavedForLater.remove(parseInt(del.dataset.del, 10));
      });
    }
    render() {
      const list = SavedForLater.all();
      const ul = $('[data-saved-list]', this);
      if (!ul) return;
      this.hidden = list.length === 0;
      const esc = (s) => String(s || '').replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));
      ul.innerHTML = list.map((i) => `<li class="saved__item">${i.image ? `<img src="${esc(i.image)}" alt="" width="56" height="56" loading="lazy">` : ''}<div class="saved__info"><a href="${esc(i.url)}">${esc(i.title)}</a>${i.variant ? `<small>${esc(i.variant)}</small>` : ''}</div><div class="saved__actions"><button type="button" class="btn btn--atc btn--xs" data-move="${i.variantId}">${esc(S.addToCart || 'Add to cart')}</button><button type="button" class="link-btn" data-del="${i.variantId}">✕</button></div></li>`).join('');
    }
  }
  customElements.define('saved-for-later', SavedForLaterEl);

  /* ------------------------------------------------ free shipping bar */
  class FreeShippingBar extends HTMLElement {
    connectedCallback() {
      const rate = (window.Shopify && Shopify.currency && parseFloat(Shopify.currency.rate)) || 1;
      if (rate === 1) return; // server render is already correct for the store currency
      const threshold = Math.round(parseInt(this.dataset.threshold, 10) * rate);
      const total = parseInt(this.dataset.total, 10) || 0;
      const remaining = threshold - total;
      const pct = Math.min(100, Math.round((total / threshold) * 100));
      const msg = $('[data-msg]', this);
      const fill = $('[data-fill]', this);
      if (fill) fill.style.width = `${pct}%`;
      this.classList.toggle('is-complete', remaining <= 0);
      if (msg) {
        const icon = msg.querySelector('svg');
        const text = remaining <= 0 ? T.freeShipping.success : (T.freeShipping.message || '').replace('[amount]', formatMoney(remaining));
        msg.textContent = text;
        if (icon) msg.prepend(icon);
      }
    }
  }
  customElements.define('free-shipping-bar', FreeShippingBar);

  /* discount code: Shopify's native /discount/CODE link applies the code to the checkout session */
  class DiscountForm extends HTMLElement {
    connectedCallback() {
      const input = $('[data-code]', this);
      const apply = () => {
        const code = input.value.trim();
        if (!code) { input.focus(); return; }
        window.location.href = `${root}discount/${encodeURIComponent(code)}?redirect=${encodeURIComponent(R.cart)}`;
      };
      $('[data-apply]', this).addEventListener('click', apply);
      input.addEventListener('keydown', (e) => { if (e.key === 'Enter') { e.preventDefault(); apply(); } });
    }
  }
  customElements.define('discount-form', DiscountForm);

  /* shipping estimator — Shopify /cart/shipping_rates.json */
  class ShippingEstimator extends HTMLElement {
    connectedCallback() {
      $('[data-estimate]', this).addEventListener('click', () => this.estimate());
    }
    async estimate() {
      const out = $('[data-results]', this);
      const btn = $('[data-estimate]', this);
      const params = new URLSearchParams({
        'shipping_address[country]': $('[data-country]', this).value,
        'shipping_address[province]': $('[data-province]', this).value,
        'shipping_address[zip]': $('[data-zip]', this).value
      });
      btn.classList.add('is-loading');
      out.textContent = '';
      try {
        const res = await fetch(`${root}cart/shipping_rates.json?${params}`);
        const data = await res.json();
        if (!res.ok) throw new Error(Object.values(data).flat().join(' ') || S.noRates);
        const rates = data.shipping_rates || [];
        out.innerHTML = rates.length
          ? `<ul>${rates.map((r) => `<li><strong>${r.name}</strong> — ${parseFloat(r.price) === 0 ? 'Free' : formatMoney(Math.round(parseFloat(r.price) * 100))}</li>`).join('')}</ul>`
          : S.noRates;
      } catch (err) {
        out.textContent = err.message || S.noRates;
      } finally { btn.classList.remove('is-loading'); }
    }
  }
  customElements.define('shipping-estimator', ShippingEstimator);

  /* cart note */
  document.addEventListener('input', debounce((e) => {
    const note = e.target.closest('[data-cart-note]');
    if (!note) return;
    fetch(`${R.cartUpdate}.js`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ note: note.value }) });
  }, 500));

  function initCartExtras(scope) {
    $$('product-recommendations', scope).forEach((el) => el.load && el.load());
    SavedForLater.renderAll();
  }

  /* ------------------------------------------------------------ hero slider */
  class HeroSlider extends HTMLElement {
    connectedCallback() {
      this.slides = $$('.hero__slide', this);
      this.dots = $$('[data-go]', this);
      this.index = 0;
      this.interval = parseInt(this.dataset.interval, 10) || 6000;
      this.style.setProperty('--hero-interval', `${this.interval}ms`);
      if (this.slides.length > 1) {
        this.addEventListener('click', (e) => {
          if (e.target.closest('[data-next]')) this.go(this.index + 1, true);
          if (e.target.closest('[data-prev]')) this.go(this.index - 1, true);
          const dot = e.target.closest('[data-go]');
          if (dot) this.go(parseInt(dot.dataset.go, 10), true);
          if (e.target.closest('[data-pause]')) this.togglePause();
        });
        this.addEventListener('keydown', (e) => { if (e.key === 'ArrowRight') this.go(this.index + 1, true); if (e.key === 'ArrowLeft') this.go(this.index - 1, true); });
        let x0 = null;
        this.addEventListener('touchstart', (e) => { x0 = e.touches[0].clientX; }, { passive: true });
        this.addEventListener('touchend', (e) => {
          if (x0 === null) return;
          const dx = e.changedTouches[0].clientX - x0;
          if (Math.abs(dx) > 45) this.go(this.index + (dx < 0 ? 1 : -1), true);
          x0 = null;
        });
        if (this.dataset.autoplay === 'true' && !reduceMotion) this.play();
        else this.classList.add('is-paused');
        this.addEventListener('mouseenter', () => this.stop());
        this.addEventListener('mouseleave', () => { if (!this.userPaused && this.dataset.autoplay === 'true' && !reduceMotion) this.play(); });
        document.addEventListener('shopify:block:select', (e) => {
          const i = this.slides.findIndex((s) => s.contains(e.target));
          if (i > -1) { this.stop(); this.go(i); }
        });
      }
      this.slides.forEach((s, i) => { if (i > 0) $$('video', s).forEach((v) => v.pause()); });
      if (this.dataset.parallax === 'true' && !reduceMotion) this.initParallax();
    }
    go(i, user) {
      const n = this.slides.length;
      const next = (i + n) % n;
      if (next === this.index) return;
      const prev = this.slides[this.index];
      prev.classList.remove('is-active');
      prev.classList.add('is-prev');
      setTimeout(() => prev.classList.remove('is-prev'), 900);
      $$('video', prev).forEach((v) => v.pause());
      this.index = next;
      const cur = this.slides[next];
      cur.classList.add('is-active');
      $$('video', cur).forEach((v) => { v.currentTime = 0; v.play().catch(() => {}); });
      this.dots.forEach((d, k) => d.classList.toggle('is-active', k === next));
      if (user && this.timer) { this.stop(); this.play(); }
    }
    play() { this.stop(); this.classList.remove('is-paused'); this.timer = setInterval(() => this.go(this.index + 1), this.interval); }
    stop() { clearInterval(this.timer); this.timer = null; this.classList.add('is-paused'); }
    togglePause() { this.userPaused = !this.userPaused; if (this.userPaused) this.stop(); else this.play(); }
    initParallax() {
      const layers = $$('[data-parallax-layer]', this);
      const onScroll = () => {
        const r = this.getBoundingClientRect();
        if (r.bottom < 0 || r.top > window.innerHeight) return;
        const y = Math.max(0, -r.top) * 0.25;
        layers.forEach((l) => { l.style.transform = `translate3d(0, ${y}px, 0)`; });
      };
      window.addEventListener('scroll', () => requestAnimationFrame(onScroll), { passive: true });
    }
  }
  customElements.define('hero-slider', HeroSlider);

  /* ------------------------------------------------------------- carousels */
  function initCarousels(scope = document) {
    $$('[data-carousel-arrows]', scope).forEach((wrap) => {
      if (wrap.dataset.bound) return;
      const track = document.getElementById(wrap.dataset.carouselArrows);
      if (!track) return;
      wrap.dataset.bound = '1';
      const [prev, next] = $$('.carousel-arrow', wrap);
      const update = () => {
        const max = track.scrollWidth - track.clientWidth - 4;
        prev.disabled = track.scrollLeft <= 4;
        next.disabled = track.scrollLeft >= max;
        wrap.hidden = track.scrollWidth <= track.clientWidth + 4;
      };
      wrap.addEventListener('click', (e) => {
        const b = e.target.closest('.carousel-arrow');
        if (!b) return;
        track.scrollBy({ left: parseInt(b.dataset.dir, 10) * track.clientWidth * 0.9, behavior: reduceMotion ? 'auto' : 'smooth' });
      });
      track.addEventListener('scroll', debounce(update, 60), { passive: true });
      window.addEventListener('resize', debounce(update, 150));
      update();
      new MutationObserver(update).observe(track, { childList: true });
    });
  }

  /* ----------------------------------------------------------- quick view */
  class QuickViewModal extends HTMLElement {
    connectedCallback() {
      this.content = $('[data-content]', this);
      this.addEventListener('click', (e) => { if (e.target.closest('[data-close]')) this.close(); });
      this.addEventListener('keydown', (e) => { if (e.key === 'Escape') this.close(); trapFocus($('.modal__dialog', this), e); });
      document.addEventListener('click', (e) => {
        const t = e.target.closest('[data-quick-view]');
        if (!t) return;
        e.preventDefault();
        this.open(t.dataset.quickView, t);
      });
    }
    async open(url, trigger) {
      this.trigger = trigger;
      this.hidden = false;
      lockScroll(true);
      this.content.innerHTML = '';
      this.content.classList.add('is-loading');
      $('.modal__dialog', this).focus();
      try {
        const html = await fetchText(`${url}${url.includes('?') ? '&' : '?'}section_id=quick-view`);
        const doc = parseHTML(html);
        const node = doc.querySelector('.qv');
        this.content.classList.remove('is-loading');
        this.content.innerHTML = '';
        if (node) this.content.appendChild(document.importNode(node, true));
        WishList.sync();
      } catch (err) {
        window.location.href = url;
      }
    }
    close() {
      if (this.hidden) return;
      this.hidden = true;
      this.content.innerHTML = '';
      lockScroll(false);
      this.trigger && this.trigger.focus && this.trigger.focus();
    }
  }
  customElements.define('quick-view-modal', QuickViewModal);

  /* ------------------------------------------------------- product info */
  class ProductInfo extends HTMLElement {
    connectedCallback() {
      this.form = document.getElementById(this.dataset.formId);
      this.addEventListener('variant:change', (e) => this.onVariant(e.detail.variant));
    }
    async onVariant(variant) {
      const idInput = this.form && this.form.querySelector('[data-variant-id]');
      const addBtn = this.form && this.form.querySelector('[data-add-button]');
      const addLabel = addBtn && addBtn.querySelector('[data-add-label]');
      if (!variant) {
        if (addBtn) { addBtn.disabled = true; if (addLabel) addLabel.textContent = S.unavailable; }
        return;
      }
      if (idInput) idInput.value = variant.id;
      if (addBtn) {
        addBtn.disabled = !variant.available;
        if (addLabel) addLabel.textContent = variant.available ? S.addToCart : S.soldOut;
      }
      $$('[data-sku]', this.closest('.shopify-section') || this).forEach((el) => { el.textContent = variant.sku || '—'; });
      document.dispatchEvent(new CustomEvent('product:variant', { detail: { variant, section: this.dataset.section } }));
      const gallery = $('media-gallery', this);
      if (gallery && variant.featured_media_id) gallery.showMedia(variant.featured_media_id);

      // Only update the URL on the real product page
      if (this.dataset.section !== 'quick-view') {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url.toString());
      }
      // Fetch the server-rendered price / stock / points for exact formatting
      try {
        const sid = this.dataset.section;
        const html = await fetchText(`${this.dataset.url}?variant=${variant.id}&section_id=${sid}`);
        const doc = parseHTML(html);
        const selectors = ['[data-price-wrap]', '[data-stock]', '.buybox__rewards', '.pdp__badges'];
        selectors.forEach((sel) => {
          const fresh = $$(sel, doc);
          $$(sel, this).forEach((el, i) => { if (fresh[i]) el.innerHTML = fresh[i].innerHTML; });
        });
        const pickupInstallments = $('.installments input[name="id"]', this);
        if (pickupInstallments) pickupInstallments.value = variant.id;
      } catch (e) { /* non-critical */ }
    }
  }
  customElements.define('product-info', ProductInfo);

  class VariantPicker extends HTMLElement {
    connectedCallback() {
      const json = $('[data-variant-json]', this);
      this.variants = json ? JSON.parse(json.textContent) : [];
      this.addEventListener('change', () => this.update());
      this.markAvailability();
    }
    selected() {
      const values = [];
      $$('.variants__option', this).forEach((fs, i) => {
        const checked = fs.querySelector('input:checked');
        const select = fs.querySelector('select');
        values[i] = checked ? checked.value : select ? select.value : null;
        const label = $(`[data-option-value="${i}"]`, this);
        if (label) label.textContent = values[i];
      });
      return values;
    }
    update() {
      const values = this.selected();
      const variant = this.variants.find((v) => v.options.every((o, i) => o === values[i]));
      this.markAvailability();
      this.dispatchEvent(new CustomEvent('variant:change', { bubbles: true, detail: { variant } }));
    }
    /* grey out option values that don't combine with the current selection into an available variant */
    markAvailability() {
      const values = this.selected();
      $$('.variants__option', this).forEach((fs, i) => {
        $$('.pill', fs).forEach((pill) => {
          const val = pill.querySelector('input').value;
          const ok = this.variants.some((v) => v.available && v.options[i] === val && v.options.every((o, k) => k === i || k > i || o === values[k]));
          pill.classList.toggle('is-unavailable', !ok);
        });
      });
    }
  }
  customElements.define('variant-picker', VariantPicker);

  class MediaGallery extends HTMLElement {
    connectedCallback() {
      this.viewport = $('[data-viewport]', this);
      this.slides = $$('.gallery__slide', this);
      this.thumbs = $$('[data-thumb]', this);
      this.dots = $$('.gallery__dot', this);
      this.addEventListener('click', (e) => {
        const t = e.target.closest('[data-thumb]');
        if (t) this.goTo(parseInt(t.dataset.thumb, 10));
      });
      if (this.viewport) {
        this.viewport.addEventListener('scroll', debounce(() => {
          const i = Math.round(this.viewport.scrollLeft / this.viewport.clientWidth);
          this.setActive(i);
        }, 60), { passive: true });
        const active = this.slides.findIndex((s) => s.classList.contains('is-active'));
        if (active > 0) requestAnimationFrame(() => this.goTo(active, true));
      }
      if (this.classList.contains('gallery--zoom') && window.matchMedia('(hover: hover)').matches) {
        $$('[data-zoom]', this).forEach((z) => {
          z.addEventListener('mousemove', (e) => {
            const r = z.getBoundingClientRect();
            z.style.setProperty('--zx', `${((e.clientX - r.left) / r.width) * 100}%`);
            z.style.setProperty('--zy', `${((e.clientY - r.top) / r.height) * 100}%`);
          });
          z.addEventListener('mouseenter', () => z.classList.add('is-zooming'));
          z.addEventListener('mouseleave', () => z.classList.remove('is-zooming'));
        });
      }
    }
    goTo(i, instant) {
      if (!this.viewport || !this.slides[i]) return;
      this.viewport.scrollTo({ left: this.slides[i].offsetLeft - this.viewport.offsetLeft, behavior: instant || reduceMotion ? 'auto' : 'smooth' });
      this.setActive(i);
    }
    setActive(i) {
      this.slides.forEach((s, k) => s.classList.toggle('is-active', k === i));
      this.thumbs.forEach((t, k) => t.classList.toggle('is-active', k === i));
      this.dots.forEach((d, k) => d.classList.toggle('is-active', k === i));
      this.slides.forEach((s, k) => { if (k !== i) $$('video', s).forEach((v) => v.pause()); });
    }
    showMedia(id) {
      const i = this.slides.findIndex((s) => s.dataset.mediaId === String(id));
      if (i > -1) this.goTo(i);
    }
  }
  customElements.define('media-gallery', MediaGallery);

  /* sticky add-to-cart bar */
  class StickyAtc extends HTMLElement {
    connectedCallback() {
      this.form = document.getElementById(this.dataset.form);
      if (!this.form) return;
      this.hidden = false;
      const target = this.form.querySelector('[data-add-button]') || this.form;
      new IntersectionObserver(([entry]) => {
        const past = !entry.isIntersecting && entry.boundingClientRect.top < 0;
        this.classList.toggle('is-visible', past);
      }).observe(target);
      $('[data-sticky-add]', this).addEventListener('click', () => {
        const btn = this.form.querySelector('[data-add-button]');
        if (btn && !btn.disabled) this.form.requestSubmit ? this.form.requestSubmit(btn) : btn.click();
      });
      document.addEventListener('product:variant', (e) => {
        const v = e.detail.variant;
        const price = $('[data-sticky-price]', this);
        if (price && v) price.textContent = formatMoney(v.price);
        const b = $('[data-sticky-add]', this);
        if (b && v) { b.disabled = !v.available; b.textContent = v.available ? S.addToCart : S.soldOut; }
      });
    }
  }
  customElements.define('sticky-atc', StickyAtc);

  /* estimated delivery (business days, respects same-day cutoff) */
  class DeliveryEstimate extends HTMLElement {
    connectedCallback() {
      const min = parseInt(this.dataset.min, 10);
      const max = parseInt(this.dataset.max, 10);
      const cutoff = parseInt(this.dataset.cutoff, 10);
      if (Number.isNaN(min) || Number.isNaN(max)) return;
      const now = new Date();
      const start = new Date(now);
      const beforeCutoff = now.getHours() < cutoff;
      if (!beforeCutoff) start.setDate(start.getDate() + 1);
      const addBusinessDays = (date, days) => {
        const d = new Date(date);
        let added = 0;
        while (d.getDay() === 0 || d.getDay() === 6) d.setDate(d.getDate() + 1);
        while (added < days) { d.setDate(d.getDate() + 1); if (d.getDay() !== 0 && d.getDay() !== 6) added++; }
        return d;
      };
      const opts = { weekday: 'short', month: 'short', day: 'numeric' };
      const locale = document.documentElement.lang || undefined;
      const a = addBusinessDays(start, min).toLocaleDateString(locale, opts);
      const b = addBusinessDays(start, max).toLocaleDateString(locale, opts);
      const out = $('[data-dates]', this);
      if (out) out.textContent = min === max ? a : `${a} – ${b}`;
      const msg = $('[data-cutoff-msg]', this);
      if (msg && beforeCutoff && now.getDay() !== 0 && now.getDay() !== 6) {
        const mins = (cutoff - now.getHours()) * 60 - now.getMinutes();
        msg.textContent = (S.cutoff || '').replace('[h]', Math.floor(mins / 60)).replace('[m]', mins % 60);
      }
    }
  }
  customElements.define('delivery-estimate', DeliveryEstimate);

  /* compatibility checker (uses the product's own compatible_models metafield) */
  class FitChecker extends HTMLElement {
    connectedCallback() {
      this.models = (this.dataset.models || '').split('||').map((m) => m.trim()).filter(Boolean);
      const input = $('input', this);
      const out = $('[data-result]', this);
      const norm = (s) => s.toLowerCase().replace(/[^a-z0-9]/g, '');
      const check = () => {
        const q = norm(input.value);
        if (!q) { out.textContent = ''; return; }
        const match = this.models.find((m) => norm(m).includes(q) || q.includes(norm(m)));
        out.classList.toggle('is-fit', !!match);
        out.classList.toggle('is-nofit', !match);
        out.textContent = match ? `✓ ${S.fits}: ${match}` : `${S.notListed}`;
      };
      $('[data-check]', this).addEventListener('click', check);
      input.addEventListener('keydown', (e) => { if (e.key === 'Enter') { e.preventDefault(); check(); } });
      input.addEventListener('change', check);
    }
  }
  customElements.define('fit-checker', FitChecker);

  /* ------------------------------------------ product recommendations */
  class ProductRecommendations extends HTMLElement {
    connectedCallback() { this.load(); }
    buildUrl() {
      if (this.dataset.url) return this.dataset.url;
      if (this.hasAttribute('data-personal')) {
        let pid = this.dataset.productId;
        if (!pid) {
          const recent = store.get('etx:recent', []);
          if (recent.length && recent[0].id) pid = recent[0].id;
        }
        if (pid) return `${this.dataset.baseUrl}&product_id=${pid}`;
      }
      return null;
    }
    async load() {
      if (this.loaded) return;
      const url = this.buildUrl();
      if (!url || /product_id=(&|$)/.test(url)) { this.finish(false); return; }
      this.loaded = true;
      try {
        let found = await this.fetchInto(url);
        if (!found && this.dataset.fallbackIntent) {
          found = await this.fetchInto(url.replace(/intent=\w+/, `intent=${this.dataset.fallbackIntent}`));
        }
        this.finish(found);
      } catch (e) { this.finish(false); }
    }
    async fetchInto(url) {
      const html = await fetchText(url);
      const doc = parseHTML(html);
      const fresh = doc.querySelector('[data-recs-content]');
      if (!fresh) return false;
      let items = $$('[data-rec-item], .build__item:not(.build__item--anchor)', fresh);
      if (this.hasAttribute('data-exclude-cart')) {
        const inCart = $$('#CartDrawer [data-handle]').map((l) => l.dataset.handle);
        items.forEach((it) => { const a = it.querySelector('a[href*="/products/"]'); if (a && inCart.some((h) => a.href.includes(`/products/${h}`))) it.remove(); });
        items = $$('[data-rec-item]', fresh);
      }
      if (!items.length) return false;
      const bundle = $('build-bundle', this);
      if (bundle) {
        // keep the anchor (current product) and swap in the recommended parts
        const list = $('.build__items', bundle);
        $$('.build__item:not(.build__item--anchor)', list).forEach((n) => n.remove());
        $$('.build__item:not(.build__item--anchor)', fresh).forEach((n) => list.appendChild(document.importNode(n, true)));
        bundle.refresh();
      } else {
        const target = $('[data-recs-content]', this);
        target.innerHTML = fresh.innerHTML;
        initCarousels(target);
        initReveal(target);
        WishList.sync();
      }
      return true;
    }
    finish(found) {
      if (!found && this.hasAttribute('data-hide-empty') && !$('[data-rec-item]', this)) {
        const sec = this.closest('.section'); if (sec) sec.hidden = true;
      }
      initReveal(this);
    }
  }
  customElements.define('product-recommendations', ProductRecommendations);

  /* ---------------------------------------------------- recently viewed */
  class RecentlyViewed extends HTMLElement {
    connectedCallback() { this.render(); $('[data-clear-recent]', this).addEventListener('click', () => { store.set('etx:recent', []); this.hidden = true; }); }
    async render() {
      const limit = parseInt(this.dataset.limit, 10) || 10;
      const exclude = this.dataset.exclude;
      const list = store.get('etx:recent', []).filter((p) => p.handle && p.handle !== exclude).slice(0, limit);
      if (!list.length) return;
      const target = $('[data-items]', this);
      const cards = await Promise.all(list.map((p) =>
        fetchText(`${root}products/${encodeURIComponent(p.handle)}?section_id=product-card-render`)
          .then((html) => parseHTML(html).querySelector('[data-card-render]'))
          .catch(() => null)));
      const valid = cards.filter(Boolean);
      if (!valid.length) return;
      target.innerHTML = '';
      valid.forEach((c) => target.appendChild(document.importNode(c, true)));
      this.hidden = false;
      initCarousels(this);
      WishList.sync();
    }
  }
  customElements.define('recently-viewed', RecentlyViewed);

  /* --------------------------------------------------------------- wishlist */
  const WishList = {
    key: 'etx:wishlist',
    all() { return store.get(this.key, []); },
    has(h) { return this.all().includes(h); },
    toggle(h) {
      let list = this.all();
      const on = !list.includes(h);
      list = on ? [h, ...list] : list.filter((x) => x !== h);
      store.set(this.key, list.slice(0, 100));
      this.sync();
      toast(on ? S.wishlistAdded : S.wishlistRemoved);
      return on;
    },
    sync() {
      const list = this.all();
      $$('[data-wishlist-toggle]').forEach((b) => {
        const on = list.includes(b.dataset.handle);
        b.setAttribute('aria-pressed', on ? 'true' : 'false');
        const label = b.querySelector('[data-wishlist-label]');
        if (label) label.textContent = on ? label.dataset.remove : label.dataset.add;
      });
      $$('[data-wishlist-count]').forEach((c) => { c.textContent = list.length; c.hidden = list.length === 0; });
    }
  };
  document.addEventListener('click', (e) => {
    const b = e.target.closest('[data-wishlist-toggle]');
    if (!b) return;
    e.preventDefault(); e.stopPropagation();
    WishList.toggle(b.dataset.handle);
    const page = b.closest('wishlist-page');
    if (page) page.render();
  });

  class WishlistPage extends HTMLElement {
    connectedCallback() { this.render(); }
    async render() {
      const handles = WishList.all();
      const grid = $('[data-items]', this);
      const empty = $('[data-empty]', this);
      empty.hidden = handles.length > 0;
      if (!handles.length) { grid.innerHTML = ''; return; }
      const cards = await Promise.all(handles.slice(0, 60).map((h) =>
        fetchText(`${root}products/${encodeURIComponent(h)}?section_id=product-card-render`)
          .then((html) => parseHTML(html).querySelector('[data-card-render]')).catch(() => null)));
      grid.innerHTML = '';
      cards.filter(Boolean).forEach((c) => { c.className = 'product-grid__item'; grid.appendChild(document.importNode(c, true)); });
      WishList.sync();
    }
  }
  customElements.define('wishlist-page', WishlistPage);

  /* ------------------------------------------------ complete your build */
  class BuildBundle extends HTMLElement {
    connectedCallback() {
      this.addEventListener('change', (e) => { if (e.target.matches('[data-build-input]')) this.refresh(); });
      $('[data-build-add]', this).addEventListener('click', (e) => this.addAll(e.currentTarget));
      this.refresh();
    }
    refresh() {
      const checked = $$('[data-build-input]:checked', this);
      const total = checked.reduce((sum, i) => sum + (parseInt(i.dataset.price, 10) || 0), 0);
      $('[data-build-total]', this).textContent = checked.length ? formatMoney(total) : '—';
      const label = $('[data-build-count-label]', this);
      if (label) label.textContent = (label.dataset.template || '').replace('[count]', checked.length);
      $('[data-build-add]', this).disabled = checked.length === 0;
    }
    async addAll(btn) {
      const items = $$('[data-build-input]:checked', this).map((i) => ({ id: parseInt(i.value, 10), quantity: 1 }));
      if (!items.length) return;
      btn.classList.add('is-loading');
      try { await Cart.add(items, btn); } catch (err) { toast(err.message, true); } finally { btn.classList.remove('is-loading'); }
    }
  }
  customElements.define('build-bundle', BuildBundle);

  /* ------------------------------------------------------ fitment finder */
  class FitmentFinder extends HTMLElement {
    connectedCallback() {
      const dataEl = $('[data-fitment-data]', this);
      try { this.models = JSON.parse(dataEl.textContent); } catch (e) { this.models = []; }
      this.brandSel = $('[data-fitment-brand]', this);
      this.modelSel = $('[data-fitment-model]', this);
      const brands = [...new Set(this.models.map((m) => m.brand).filter(Boolean))];
      brands.forEach((b) => this.brandSel.add(new Option(b, b)));
      this.brandSel.addEventListener('change', () => this.fillModels());
      const chips = $('[data-fitment-chips]', this);
      if (chips) {
        brands.forEach((b) => {
          const btn = document.createElement('button');
          btn.type = 'button'; btn.textContent = b;
          btn.addEventListener('click', () => {
            this.brandSel.value = b; this.fillModels();
            $$('button', chips).forEach((x) => x.classList.toggle('is-active', x === btn));
            this.modelSel.focus();
          });
          chips.appendChild(btn);
        });
      }
      $('[data-fitment-form]', this).addEventListener('submit', (e) => {
        const m = this.models.find((x) => x.brand === this.brandSel.value && x.model === this.modelSel.value);
        if (!m) { e.preventDefault(); (this.brandSel.value ? this.modelSel : this.brandSel).focus(); return; }
        try { store.set('etx:mybike', m); } catch (err) { /* ignore */ }
        if (m.url) { e.preventDefault(); window.location.href = m.url; return; }
        $('[data-fitment-q]', this).value = `${m.brand === 'Custom build' ? '' : m.brand + ' '}${m.model}`.trim();
      });
      const saved = store.get('etx:mybike', null);
      if (saved && brands.includes(saved.brand)) { this.brandSel.value = saved.brand; this.fillModels(saved.model); }
    }
    fillModels(selected) {
      const brand = this.brandSel.value;
      this.modelSel.length = 1;
      this.models.filter((m) => m.brand === brand).forEach((m) => this.modelSel.add(new Option(m.model, m.model, false, m.model === selected)));
      this.modelSel.disabled = !brand;
    }
  }
  customElements.define('fitment-finder', FitmentFinder);

  /* ---------------------------------------------------- deal countdown */
  class DealCountdown extends HTMLElement {
    connectedCallback() {
      const end = new Date(this.dataset.end.replace(' ', 'T'));
      if (Number.isNaN(end.getTime())) return;
      const out = $('[data-time]', this);
      const tick = () => {
        const diff = end - new Date();
        if (diff <= 0) { this.hidden = true; clearInterval(this.timer); return; }
        const d = Math.floor(diff / 864e5);
        const h = Math.floor((diff % 864e5) / 36e5);
        const m = Math.floor((diff % 36e5) / 6e4);
        const s = Math.floor((diff % 6e4) / 1e3);
        const pad = (n) => String(n).padStart(2, '0');
        out.textContent = `${d ? d + 'd ' : ''}${pad(h)}:${pad(m)}:${pad(s)}`;
        this.hidden = false;
      };
      tick();
      this.timer = setInterval(tick, 1000);
    }
    disconnectedCallback() { clearInterval(this.timer); }
  }
  customElements.define('deal-countdown', DealCountdown);

  /* ------------------------------------------------------ filters & sort */
  class FacetDrawer extends HTMLElement {
    connectedCallback() {
      this.addEventListener('click', (e) => { if (e.target.closest('[data-close]')) this.close(); });
      this.addEventListener('keydown', (e) => { if (e.key === 'Escape') this.close(); });
    }
    open() { this.classList.add('is-open'); lockScroll(true); }
    close() { if (!this.classList.contains('is-open')) return; this.classList.remove('is-open'); lockScroll(false); }
  }
  customElements.define('facet-drawer', FacetDrawer);
  document.addEventListener('click', (e) => {
    const b = e.target.closest('[data-open-facets]');
    if (b) { const d = document.getElementById(b.dataset.openFacets); d && d.open(); }
  });
  document.addEventListener('change', (e) => {
    const t = e.target;
    if (t.matches('[data-autosubmit]')) {
      const form = t.form;
      if (!form) return;
      if (form.matches('[data-facets-form]')) {
        // drop empty price inputs so URLs stay clean
        $$('input[type="number"]', form).forEach((i) => { if (!i.value) i.disabled = true; });
      }
      form.submit();
    }
    if (t.matches('[data-sort]')) {
      const url = new URL(window.location.href);
      url.searchParams.set('sort_by', t.value);
      url.searchParams.delete('page');
      window.location.href = url.toString();
    }
  });
  document.addEventListener('submit', (e) => {
    const form = e.target.closest('[data-facets-form]');
    if (form) $$('input[type="number"]', form).forEach((i) => { if (!i.value) i.disabled = true; });
  });

  /* ------------------------------------------------------ misc elements */
  class DeferredVideo extends HTMLElement {
    connectedCallback() {
      $('button', this).addEventListener('click', () => {
        const id = encodeURIComponent(this.dataset.id);
        const src = this.dataset.type === 'vimeo'
          ? `https://player.vimeo.com/video/${id}?autoplay=1`
          : `https://www.youtube-nocookie.com/embed/${id}?autoplay=1&rel=0`;
        this.innerHTML = `<iframe src="${src}" allow="autoplay; encrypted-media; picture-in-picture" allowfullscreen title="Video" style="width:100%;height:100%;border:0"></iframe>`;
      });
    }
  }
  customElements.define('deferred-video', DeferredVideo);

  class ShareButton extends HTMLElement {
    connectedCallback() {
      const b = $('[data-share]', this);
      b.addEventListener('click', async () => {
        const data = { title: b.dataset.title, url: b.dataset.url };
        try {
          if (navigator.share) await navigator.share(data);
          else { await navigator.clipboard.writeText(data.url); toast(S.linkCopied); }
        } catch (e) { /* cancelled */ }
      });
    }
  }
  customElements.define('share-button', ShareButton);

  /* localization selects submit immediately */
  document.addEventListener('change', (e) => {
    if (e.target.matches('.localization [data-autosubmit]')) e.target.form.submit();
  });

  /* address forms: province selector from Shopify's all_country_option_tags data */
  $$('[data-country-select]').forEach((sel) => {
    const form = sel.closest('form');
    const prov = form.querySelector('[data-province-select]');
    const wrap = form.querySelector('[data-province-wrap]');
    if (sel.dataset.default) sel.value = sel.dataset.default;
    const fill = () => {
      const opt = sel.options[sel.selectedIndex];
      let provinces = [];
      try { provinces = JSON.parse((opt && opt.dataset.provinces) || '[]'); } catch (err) { provinces = []; }
      prov.innerHTML = '';
      provinces.forEach(([value, label]) => prov.add(new Option(label, value, false, value === prov.dataset.default)));
      if (wrap) wrap.hidden = provinces.length === 0;
    };
    sel.addEventListener('change', fill);
    fill();
  });
  document.addEventListener('submit', (e) => {
    const f = e.target.closest('form[data-confirm]');
    if (f && !window.confirm(f.dataset.confirm)) e.preventDefault();
  });

  /* ------------------------------------------------------------- bootstrap */
  function init(scope = document) {
    // product details jump-nav: drop links to panels that aren't rendered for this product
    $$('.pdd__nav a', scope).forEach((a) => { if (!document.getElementById(a.getAttribute('href').slice(1))) a.remove(); });
    initReveal(scope);
    initCarousels(scope);
    WishList.sync();
    SavedForLater.renderAll();
  }
  init();

  /* 3D models: load Shopify's model viewer UI only when a product has 3D media */
  if (document.querySelector('model-viewer') && window.Shopify && Shopify.loadFeatures) {
    Shopify.loadFeatures([
      { name: 'model-viewer-ui', version: '1.0', onLoad: (e) => { if (!e) $$('model-viewer').forEach((m) => new Shopify.ModelViewerUI(m)); } },
      { name: 'shopify-xr', version: '1.0' }
    ]);
  }

  /* Theme editor support: re-init sections when they're added/edited */
  document.addEventListener('shopify:section:load', (e) => init(e.target));
  document.addEventListener('shopify:section:select', (e) => {
    const d = e.target.querySelector('cart-drawer'); if (d) d.open();
  });
  document.addEventListener('shopify:section:deselect', (e) => {
    const d = e.target.querySelector('cart-drawer'); if (d) d.close();
  });
})();
